import React from 'react';
import { 
  Grid, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  FormHelperText 
} from '@mui/material';

const PersonalInfo = ({ employee, isEditable, handleChange, errors, roleOptions, tiers = [] }) => {
  if (!employee) {
    return <div>Loading employee information...</div>;
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="First Name"
          name="first_name"
          value={employee.first_name || ''}
          onChange={handleChange}
          disabled={!isEditable}
          error={!!errors.first_name}
          helperText={errors.first_name}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Last Name"
          name="last_name"
          value={employee.last_name || ''}
          onChange={handleChange}
          disabled={!isEditable}
          error={!!errors.last_name}
          helperText={errors.last_name}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Email"
          name="email"
          type="email"
          value={employee.email || ''}
          onChange={handleChange}
          disabled={!isEditable}
          error={!!errors.email}
          helperText={errors.email}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth error={!!errors.role}>
          <InputLabel id="role-label">System Access Role</InputLabel>
          <Select
            labelId="role-label"
            name="role"
            value={employee.role || ''}
            onChange={handleChange}
            disabled={!isEditable}
            label="System Access Role"
          >
            {roleOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {errors.role && <FormHelperText>{errors.role}</FormHelperText>}
          <FormHelperText>Controls what this person can access in the system</FormHelperText>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Job Title"
          name="job_role"
          value={employee.job_role || ''}
          onChange={handleChange}
          disabled={!isEditable}
          placeholder="e.g. Senior Stylist, Colourist, Junior"
          helperText="Displayed on leave balances and reports"
        />
      </Grid>

      {/* Pricing Tier — links this employee to a calculator tier for service pricing */}
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel id="tier-label">Pricing Tier</InputLabel>
          <Select
            labelId="tier-label"
            name="tier_id"
            value={employee.tier_id || ''}
            onChange={handleChange}
            disabled={!isEditable}
            label="Pricing Tier"
          >
            <MenuItem value="">
              <em>No tier assigned</em>
            </MenuItem>
            {tiers.map((tier) => (
              <MenuItem key={tier.id} value={tier.id}>
                {tier.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {tiers.length === 0
              ? 'No tiers set up yet — add them in the Pricing Calculator'
              : 'Determines the price charged for this employee\'s services'}
          </FormHelperText>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Address Line 1"
          name="address_line1"
          value={employee.address_line1 || ''}
          onChange={handleChange}
          disabled={!isEditable}
          error={!!errors.address_line1}
          helperText={errors.address_line1}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Address Line 2"
          name="address_line2"
          value={employee.address_line2 || ''}
          onChange={handleChange}
          disabled={!isEditable}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Address Line 3"
          name="address_line3"
          value={employee.address_line3 || ''}
          onChange={handleChange}
          disabled={!isEditable}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Postcode"
          name="postcode"
          value={employee.postcode || ''}
          onChange={handleChange}
          disabled={!isEditable}
          error={!!errors.postcode}
          helperText={errors.postcode}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Contact Number"
          name="contact_number"
          value={employee.contact_number || ''}
          onChange={handleChange}
          disabled={!isEditable}
          error={!!errors.contact_number}
          helperText={errors.contact_number}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Emergency Contact"
          name="emergency_contact"
          value={employee.emergency_contact || ''}
          onChange={handleChange}
          disabled={!isEditable}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Emergency Number"
          name="emergency_number"
          value={employee.emergency_number || ''}
          onChange={handleChange}
          disabled={!isEditable}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Date of Birth"
          name="date_of_birth"
          type="date"
          value={employee.date_of_birth || ''}
          onChange={handleChange}
          disabled={!isEditable}
          InputLabelProps={{ shrink: true }}
          error={!!errors.date_of_birth}
          helperText={errors.date_of_birth}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="National Insurance Number"
          name="national_insurance_number"
          value={employee.national_insurance_number || ''}
          onChange={handleChange}
          disabled={!isEditable}
          error={!!errors.national_insurance_number}
          helperText={errors.national_insurance_number}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Start Date"
          name="start_date"
          type="date"
          value={employee.start_date || ''}
          onChange={handleChange}
          disabled={!isEditable}
          InputLabelProps={{ shrink: true }}
          error={!!errors.start_date}
          helperText={errors.start_date}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Hourly Rate"
          name="hourly_rate"
          type="number"
          value={employee.hourly_rate || ''}
          onChange={handleChange}
          disabled={!isEditable}
          error={!!errors.hourly_rate}
          helperText={errors.hourly_rate}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Commission % on Services"
          name="commission_services"
          type="number"
          value={employee.commission_services || ''}
          onChange={handleChange}
          disabled={!isEditable}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Commission % on Products"
          name="commission_products"
          type="number"
          value={employee.commission_products || ''}
          onChange={handleChange}
          disabled={!isEditable}
        />
      </Grid>
    </Grid>
  );
};

export default PersonalInfo;